import { supabase } from './supabase';

interface SendNotificationParams {
  user_id: string;
  type: string;
  titre: string;
  message: string;
  lien?: string;
  metadata?: any;
}

export async function sendNotification(params: SendNotificationParams) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) return { success: false, error: 'Pas de session active' };

    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(params)
      }
    );

    const result = await response.json();
    
    if (!response.ok) return { success: false, error: result.error };

    return { success: true, data: result };
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// Fonctions spécifiques pour chaque type de notification

export async function notifyNewSuivi(medecinId: string, patientNom: string, patientPrenom: string, suiviId: string) {
  return sendNotification({
    user_id: medecinId,
    type: 'suivi',
    titre: 'Nouveau suivi médical',
    message: `${patientNom} ${patientPrenom} a créé un nouveau suivi médical`,
    lien: '/medecin/rendez-vous',
    metadata: { suiviId }
  });
}

export async function notifyNewRendezVous(medecinId: string, patientNom: string, patientPrenom: string, date: string, heure: string) {
  return sendNotification({
    user_id: medecinId,
    type: 'rendez-vous',
    titre: 'Nouveau rendez-vous',
    message: `${patientNom} ${patientPrenom} a pris rendez-vous le ${date} à ${heure}`,
    lien: '/medecin/rendez-vous',
    metadata: { date, heure }
  });
}

export async function notifyRendezVousConfirmed(patientId: string, medecinNom: string, date: string, heure: string) {
  return sendNotification({
    user_id: patientId,
    type: 'rendez-vous',
    titre: 'Rendez-vous confirmé',
    message: `Votre rendez-vous avec Dr. ${medecinNom} le ${date} à ${heure} est confirmé`,
    lien: '/patient/rendez-vous',
    metadata: { date, heure }
  });
}

export async function notifyRendezVousCancelled(userId: string, role: string, date: string, heure: string, reason?: string) {
  const message = reason 
    ? `Rendez-vous du ${date} à ${heure} annulé. Raison: ${reason}`
    : `Rendez-vous du ${date} à ${heure} annulé`;
    
  return sendNotification({
    user_id: userId,
    type: 'rendez-vous',
    titre: 'Rendez-vous annulé',
    message,
    lien: role === 'medecin' ? '/medecin/rendez-vous' : '/patient/rendez-vous',
    metadata: { date, heure, reason }
  });
}

export async function notifyNewPrescription(patientId: string, medecinNom: string) {
  return sendNotification({
    user_id: patientId,
    type: 'prescription',
    titre: 'Nouvelle prescription',
    message: `Dr. ${medecinNom} vous a prescrit de nouveaux médicaments`,
    lien: '/patient/rendez-vous',
    metadata: { medecinNom }
  });
}

export async function notifyRendezVousReminder(userId: string, role: string, medecinNom: string, date: string, heure: string) {
  const message = role === 'patient'
    ? `Rappel: Rendez-vous avec Dr. ${medecinNom} demain à ${heure}`
    : `Rappel: Rendez-vous demain à ${heure}`;
    
  return sendNotification({
    user_id: userId,
    type: 'rappel',
    titre: 'Rappel de rendez-vous',
    message,
    lien: role === 'medecin' ? '/medecin/rendez-vous' : '/patient/rendez-vous',
    metadata: { date, heure }
  });
}

export async function notifyDoctorInvitation(email: string, cliniqueName: string) {
  // Cette notification sera envoyée par email
}

export async function notifySuiviResponse(patientId: string, medecinNom: string) {
  return sendNotification({
    user_id: patientId,
    type: 'suivi',
    titre: 'Réponse à votre suivi',
    message: `Dr. ${medecinNom} a répondu à votre suivi médical`,
    lien: '/patient/rendez-vous',
    metadata: { medecinNom }
  });
}
